/** Credenciales de un solo uso que viajan en la URL (`?token=`, `/registro/{token}`): canjeables por una
 * sesión, no pueden salir hacia PostHog ni Sentry, que meten la URL en muchos campos distintos. */

const REDACTADO = '[REDACTADO]'

/** `token` es el único que usan las rutas de hoy; los demás cubren enlaces futuros. */
const PARAMETROS_SENSIBLES = ['token', 'access_token', 'refresh_token', 'codigo_acceso', 'invitacion']

const QUERY_SENSIBLE = new RegExp(`([?&](?:${PARAMETROS_SENSIBLES.join('|')})=)[^&#\\s"']*`, 'gi')

/** `/registro/empresa` es la pantalla pública de autorregistro, no un secreto. */
const RUTA_REGISTRO = /(\/registro\/)(?!empresa(?:[/?#]|$))[^/?#\s"']+/gi

/** `/clave#<código>` y `/verificar-correo#<código>`, con o sin consulta antes del `#`: enlaces de un uso de backend-v2. */
const FRAGMENTO_DE_ENLACE = /(\/(?:clave|verificar-correo)(?:\?[^#\s"']*)?#)[^\s"']+/gi

/** Acota el costo y corta cualquier ciclo sin llevar registro de visitados. */
const PROFUNDIDAD_MAX = 10

/** rrweb anida dos objetos por nivel de DOM (~250 niveles). Tocar el piso descarta el evento. */
const PROFUNDIDAD_ARBOL_DOM = 500

/** `$snapshot_data` va por `redactarFlujoRrweb`, con su propia cota de profundidad. */
const SIN_RECORRER = new Set(['$snapshot_data'])

export function redactarUrl(texto: string): string {
  return texto
    .replace(QUERY_SENSIBLE, `$1${REDACTADO}`)
    .replace(RUTA_REGISTRO, `$1${REDACTADO}`)
    .replace(FRAGMENTO_DE_ENLACE, `$1${REDACTADO}`)
}

/** `false` si tocó el piso de profundidad y quedó parte del valor sin revisar. */
function recorrer(valor: unknown, ignoradas: ReadonlySet<string> | undefined, restante: number): boolean {
  if (valor === null || typeof valor !== 'object') return true
  if (restante === 0) return false
  const registro = valor as Record<string, unknown>
  let completo = true
  for (const clave of Object.keys(registro)) {
    if (ignoradas?.has(clave)) continue
    const hijo = registro[clave]
    if (typeof hijo === 'string') registro[clave] = redactarUrl(hijo)
    else if (!recorrer(hijo, ignoradas, restante - 1)) completo = false
  }
  return completo
}

/** Redacta en el sitio toda cadena del evento; no enumera campos a propósito, para atrapar los nuevos. */
export function redactarValor(valor: unknown, ignoradas?: ReadonlySet<string>): void {
  recorrer(valor, ignoradas, PROFUNDIDAD_MAX)
}

/** Camina el árbol completo: ninguna opción de rrweb enmascara atributos (el `href` del enlace de
 * registro viaja crudo); medido en ~1 ms sobre 833 KB. `false` = el evento no se puede enviar. */
export function redactarFlujoRrweb(flujo: unknown): boolean {
  if (flujo === undefined || flujo === null) return true
  if (typeof flujo !== 'object') return false
  return recorrer(flujo, undefined, PROFUNDIDAD_ARBOL_DOM)
}

/** Fallan cerrado: si la redacción se rompe, se descarta el evento en vez de mandarlo sin redactar. */
export function redactarEventoSentry(evento: unknown): boolean {
  try {
    redactarValor(evento)
    return true
  } catch {
    return false
  }
}

export function redactarPropiedadesPosthog(propiedades: Record<string, unknown>): boolean {
  try {
    redactarValor(propiedades, SIN_RECORRER)
    return redactarFlujoRrweb(propiedades.$snapshot_data)
  } catch {
    return false
  }
}

/** No es la red principal: PostHog congela `$session_entry_url` antes de que monte la página. Esto
 * limpia el historial del navegador y lo que se ve en pantalla. */
export function limpiarCredencialDeLaBarra(): void {
  const {pathname, search, hash} = window.location
  const parametros = new URLSearchParams(search)
  const sobrantes = PARAMETROS_SENSIBLES.filter((nombre) => parametros.has(nombre))
  if (sobrantes.length === 0) return
  for (const nombre of sobrantes) parametros.delete(nombre)
  const consulta = parametros.toString()
  window.history.replaceState(null, '', `${pathname}${consulta ? `?${consulta}` : ''}${hash}`)
}
